/**
 * Fetches all paginated results from a DRF-style paginated API endpoint.
 *
 * This function will recursively follow the `next` links provided by the API until
 * all results are retrieved or the maximum number of pages is reached.
 *
 * @param {string} url - The base API URL to start fetching results from.
 * @param {Record<string, any>} params - Optional query parameters to include with the first request.
 * @param {number} [maxPages=30] - Optional maximum number of pages to fetch. Defaults to 30.
 * @returns {Promise<any[]>} - A promise that resolves to an array of all combined results.
 *
 * @example
 * const users = await fetchAllPaginatedResults('/api/users/', { active: true });
 * dispatch(setUsers(users));
 */
import axiosClient from "../axios";

export const fetchAllPaginatedResults = async (
    url: string,
    params: Record<string, any> = {},
    maxPages?: number
): Promise<any[]> => {
    let results: any[] = [];
    let pageCount = 0;
    const max = maxPages ?? 30;

    const fetchPage = async (pageUrl: string): Promise<void> => {
        if (pageCount >= max) return;

        try {
            const response = await axiosClient.get(pageUrl, { params });
            results = results.concat(response.data?.results || []);
            pageCount++;

            if (response.data?.next && pageCount < max) {
                await fetchPage(response.data.next);
            }
        } catch (error) {
            console.error('Error fetching paginated results:', error);
            throw error;
        }
    };

    await fetchPage(url);
    return results;
};
